import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";

import "../PickupDeliveryProofDialog/PickupDeliveryProofDialog.scss";
import { submitPOD } from "api";
import { useGetShipmentInvoices } from "api/queries/invoice/useGetShipmentInvoices";
import { useGetDeliveryStopsPod } from "api/queries/shipments/useGetDeliveryStopsPod";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import Modal from "components/v2/Modal/Modal";
import Button from "components/v2/Button/Button";
import usePermissions from "hooks/usePermissions";
import errorToast from "helpers/errorToast";
import { getTimezoneOffset } from "helpers/dateTimeHelper";
import {
  hasSubmittedIntermediateInvoice,
  IntermediateInvoiceLike,
} from "helpers/hasSubmittedIntermediateInvoice";
import testIds from "constants/testIds";
import ProofOfDeliverySharedFields from "../PickupDeliveryProofDialog/components/ProofOfDeliverySharedFields";
import { getDelayReasons, isPodAttachmentsValid } from "../PickupDeliveryProofDialog/utils/podFormUtils";
import MultistopPodTypeField, { PodType } from "./components/MultistopPodTypeField";
import MultistopDeliveryStopSelect from "./components/MultistopDeliveryStopSelect";
import MarkShipmentDeliveredField from "./components/MarkShipmentDeliveredField";
import { isMultistopPodFinalizing } from "./isMultistopPodFinalizing";

export type MultistopProofOfDeliveryDialogProps = Readonly<{
  show: boolean;
  onHide: () => void;
  shipmentId: string;
  initialPodType?: PodType;
  initialStopId?: number;
  setIsShipmentDelivered?: Dispatch<SetStateAction<boolean>>;
  refetchFn?: () => void;
}>;

type MultistopPodFormValues = {
  pod_type: PodType;
  stop_id: number | null;
  mark_shipment_as_done: boolean;
  delivery_date?: string;
  delivery_time?: string;
  acceptor_name?: string;
  billing_status?: string;
  file_attachments: any[];
  delay_reason?: Record<string, boolean>;
  selected_attachments?: Record<string, boolean>;
};

type PodDialogTarget = Readonly<{
  podType: PodType;
  stopId: number | null;
}>;

function buildPodDialogTarget(podType: PodType, stopId?: number): PodDialogTarget {
  return {
    podType,
    stopId: podType === "per_stop" ? stopId ?? null : null,
  };
}

function isSamePodDialogTarget(previous: PodDialogTarget | null, next: PodDialogTarget) {
  if (!previous) return false;
  return previous.podType === next.podType && previous.stopId === next.stopId;
}

function getInitialPodFormValues(target: PodDialogTarget): MultistopPodFormValues {
  return {
    pod_type: target.podType,
    stop_id: target.stopId,
    mark_shipment_as_done: false,
    file_attachments: [],
    delivery_date: "",
    delivery_time: "",
    acceptor_name: "",
    billing_status: undefined,
    delay_reason: {},
    selected_attachments: {},
  };
}

function shouldMarkShipmentDelivered(formData: MultistopPodFormValues) {
  if (formData.pod_type === "master") return true;
  return formData.mark_shipment_as_done;
}

export default function MultistopProofOfDeliveryDialog({
  show,
  onHide,
  shipmentId,
  initialPodType = "master",
  initialStopId,
  setIsShipmentDelivered,
  refetchFn,
}: MultistopProofOfDeliveryDialogProps) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const lastTargetRef = useRef<PodDialogTarget | null>(null);
  // Remount shared fields (incl. attachment checkboxes) when the open target changes.
  // Checkbox uses uncontrolled defaultChecked, so reset alone does not clear toggles in the DOM.
  const [fieldsSessionKey, setFieldsSessionKey] = useState(0);

  const methods = useForm<MultistopPodFormValues>({
    defaultValues: getInitialPodFormValues(buildPodDialogTarget("master")),
  });
  const { handleSubmit, reset, watch, setValue } = methods;

  const podType = watch("pod_type");
  const selectedStopId = watch("stop_id");
  const markShipmentAsDone = watch("mark_shipment_as_done");
  const isPerStop = podType === "per_stop";

  const { data: stopsData } = useGetDeliveryStopsPod(shipmentId, {
    enabled: show && !!shipmentId,
    refetchOnMount: false,
  });
  const stops = stopsData?.stops ?? [];

  const { data: shipmentData, isFetching: isFetchingShipment }: any = useGetShipmentBasic(shipmentId, {
    enabled: show && !!shipmentId,
    refetchOnMount: false,
  });
  const allowProviderToSkipInvoiceSubmission =
    shipmentData?.organization?.allow_provider_to_skip_invoice_submission ?? false;

  // Refetch on open: a list cached before the intermediate invoice existed would hide the radios.
  const { data: invoices, isFetching: isFetchingInvoices } = useGetShipmentInvoices({
    shipmentId,
    options: {
      enabled: show && !!shipmentId && allowProviderToSkipInvoiceSubmission,
      select: (data: { results?: IntermediateInvoiceLike[] }) => data.results,
    },
  });

  const isFinalizingPod = isMultistopPodFinalizing({
    podType,
    selectedStopId,
    stops,
    markShipmentAsDone,
  });

  const showBillingStatus =
    allowProviderToSkipInvoiceSubmission && hasSubmittedIntermediateInvoice(invoices) && isFinalizingPod;

  // A finalizing POD cannot be submitted before the invoice list settles, otherwise an unknown
  // list reads as "no invoice" and the POD ships without a billing status.
  const isAwaitingBillingStatusData = isFinalizingPod && (isFetchingShipment || isFetchingInvoices);

  useEffect(() => {
    if (showBillingStatus) return;
    setValue("billing_status", undefined);
  }, [showBillingStatus, setValue]);

  const selectedStop = useMemo(
    () => (selectedStopId != null ? stops.find((stop) => stop.stop_id === selectedStopId) : undefined),
    [stops, selectedStopId],
  );

  const timezoneOffset = getTimezoneOffset(selectedStop?.timezone || shipmentData?.schedule?.stop1_timezone || "UTC");
  const { quotedDeliveryDate, quotedDeliveryTime } = useMemo(() => {
    if (isPerStop) {
      return {
        quotedDeliveryDate: selectedStop?.provider_date ?? undefined,
        quotedDeliveryTime: selectedStop?.provider_time ?? undefined,
      };
    }

    return {
      quotedDeliveryDate: shipmentData?.schedule?.stop1_quoted_delivery_date ?? undefined,
      quotedDeliveryTime: shipmentData?.schedule?.stop1_quoted_delivery_time ?? undefined,
    };
  }, [
    isPerStop,
    selectedStop?.provider_date,
    selectedStop?.provider_time,
    shipmentData?.schedule?.stop1_quoted_delivery_date,
    shipmentData?.schedule?.stop1_quoted_delivery_time,
  ]);

  useEffect(() => {
    if (!show) return;

    const target = buildPodDialogTarget(initialPodType, initialStopId);
    if (!isSamePodDialogTarget(lastTargetRef.current, target)) {
      reset(getInitialPodFormValues(target));
      lastTargetRef.current = target;
      setFieldsSessionKey((key) => key + 1);
    }
  }, [show, initialPodType, initialStopId, reset]);

  const buildPodPayload = (formData: MultistopPodFormValues) => {
    const delayReasons = getDelayReasons(formData.delay_reason);

    return {
      acceptor_name: formData.acceptor_name,
      delivery_date: formData.delivery_date,
      delivery_time: formData.delivery_time,
      file_attachments: formData.file_attachments,
      ...(formData.billing_status === "paid" || formData.billing_status === "unpaid"
        ? { billing_status: formData.billing_status }
        : {}),
      pod_type: formData.pod_type,
      ...(delayReasons && { delay_reason: delayReasons }),
      ...(formData.pod_type === "per_stop" && {
        stop_id: formData.stop_id,
        mark_shipment_as_done: formData.mark_shipment_as_done,
      }),
    };
  };

  const onSubmit = (formData: MultistopPodFormValues) => {
    if (!isPodAttachmentsValid(formData)) {
      return;
    }

    setIsSubmitting(true);

    submitPOD(shipmentId, buildPodPayload(formData))
      .then(() => {
        if (shouldMarkShipmentDelivered(formData)) {
          toast(
            intl.formatMessage({
              id: "components.molecules.top-nav.deliveryCompleted",
              defaultMessage: "Delivery marked as complete",
            }),
            { type: "success" },
          );
          if (setIsShipmentDelivered) setIsShipmentDelivered(true);
        } else {
          toast(
            intl.formatMessage({
              id: "components.molecules.top-nav.stopPodSubmitted",
              defaultMessage: "Stop POD submitted successfully",
            }),
            { type: "success" },
          );
        }
        setTimeout(() => {
          if (refetchFn) refetchFn();
        }, 3000);
        onHide();
      })
      .catch((err) => {
        errorToast(err);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header>
        <div className="text-primary text-xl font-semibold">
          <FormattedMessage id="components.molecules.top-nav.proofOfDelivery" defaultMessage="Proof of delivery" />
        </div>
        <div className="text-tertiary text-sm mb-2xl">
          {user_type === "shipper" ? (
            <FormattedMessage
              id="components.molecules.top-nav.notifyStatusUpdateProvider"
              defaultMessage="We will notify the provider of the status update."
            />
          ) : (
            <FormattedMessage
              id="components.molecules.top-nav.notifyStatusUpdateShipper"
              defaultMessage="We will notify the shipper of the status update."
            />
          )}
        </div>
      </Modal.Header>
      <Modal.Body>
        <FormProvider {...methods}>
          <form id="submit-multistop-pod-form" data-testid={testIds.SHIPMENT_DASHBOARD_MULTISTOP_POD_DIALOG}>
            <div className="pod-container d-flex flex-column gap-3xl">
              <MultistopPodTypeField />
              {isPerStop && (
                <>
                  <MultistopDeliveryStopSelect stops={stops} />
                  <MarkShipmentDeliveredField />
                </>
              )}
              <ProofOfDeliverySharedFields
                key={fieldsSessionKey}
                quotedDeliveryDate={quotedDeliveryDate}
                quotedDeliveryTime={quotedDeliveryTime}
                timezoneOffset={timezoneOffset}
                showBillingStatus={showBillingStatus}
                showCompletionWarning={false}
                layout="stacked"
              />
            </div>
          </form>
        </FormProvider>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex align-items-center justify-content-end">
          <div className="mr-lg">
            <Button
              label={intl.formatMessage({ id: "general.back", defaultMessage: "Back" })}
              onClick={onHide}
              variant="secondary-gray"
            />
          </div>
          <Button
            label={intl.formatMessage({ id: "general.confirm", defaultMessage: "Confirm" })}
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting || isAwaitingBillingStatusData}
            testId={testIds.SHIPMENT_DASHBOARD_MULTISTOP_POD_CONFIRM_BUTTON}
          />
        </div>
      </Modal.Footer>
    </Modal>
  );
}
